import React, {
  forwardRef,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import Menu from '@material-ui/core/Menu';
import { usePrompts } from '@eas/common-web';
import { useWorkOrderDetails } from '@modules/planner/work-order-menu/work-order-details';
import { useWorkOrderExceptions } from '@composite/planner/work-order-exceptions';
import { LockWorkOrderMenuItem } from '@composite/work-order/context-menu/lock-work-order-menu-item';
import { UnplanWorkOrderMenuItem } from '@composite/work-order/context-menu/unplan-work-order-menu-item';
import { WorkOrder } from '@models';
import { PlanContext } from '../plan-context';
import { DetailsMenuItem } from './menu-items/details';
import { ErrorsMenuItem } from './menu-items/errors';
import { OpenMenuItem } from './menu-items/open';
import { RouteMenuItem } from './menu-items/route';
import { StateMenuItem } from './menu-items/state';
import { useTravelTo } from './work-order-travel-to';

type WorkOrderMenuProps = {
  previousWorkOrder?: WorkOrder;
  nextWorkOrder?: WorkOrder;
  workOrder: WorkOrder;
  stickToPointer?: boolean;
};

export type WorkOrderMenuHandle = {
  open: (event: React.MouseEvent<HTMLElement>) => void;
};

export const WorkOrderMenu = forwardRef(function WorkOrderMenu(
  {
    previousWorkOrder,
    nextWorkOrder,
    workOrder,
    stickToPointer,
  }: WorkOrderMenuProps,
  ref: React.Ref<WorkOrderMenuHandle>
) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeMenu = () => setAnchorEl(null);

  const [left, setLeft] = useState(0);

  const { calendarSource } = useContext(PlanContext);

  useImperativeHandle(ref, () => ({
    open: (event: React.MouseEvent<HTMLElement>) => {
      if (stickToPointer) {
        setLeft(event.nativeEvent.clientX);
      }
      setAnchorEl(event.currentTarget);
    },
  }));

  useWorkOrderDetails({
    id: workOrder.id,
    showConfirm: false,
  });

  useWorkOrderExceptions({
    id: workOrder.id,
    exceptions: workOrder.validationErrors ?? [],
    showConfirm: false,
    showText: false,
  });

  const getUnitStartingPoint = (
    workOrder: WorkOrder
  ): { latitude?: number; longitude?: number } | undefined => {
    if (workOrder.user?.startingPoint) {
      return workOrder.user?.startingPoint;
    }
    if (workOrder.mobileUnit?.startingPoint) {
      return workOrder.mobileUnit?.startingPoint;
    }
    return undefined;
  };
  useTravelTo({
    id: workOrder.id,
    workOrders: [previousWorkOrder, workOrder],
    unitStartingPoint: getUnitStartingPoint(workOrder),
    addStartingPoint: previousWorkOrder === undefined,
    addReturnPoint: nextWorkOrder === undefined,
  });

  usePrompts([
    {
      key: `CHANGE_STATE_ALERT_${workOrder.id}`,
      dialogTitle: 'Manuálna zmena stavu',
      dialogShowClose: true,
      dialogShowConfirm: true,
      dialogWidth: 400,
      dialogText: 'Naozaj chcete zmeniť stav PP?',
    },
  ]);

  usePrompts(
    [
      {
        key: `WORK_ORDER_LOCK_ALERT_${workOrder.id}`,
        dialogTitle: 'Zamknúť / odomknúť pracovný príkaz',
        dialogShowClose: true,
        dialogShowConfirm: true,
        dialogWidth: 400,
        dialogText: (
          <div>
            Naozaj chcete <b>{workOrder.locked ? 'odomknúť' : 'zamknúť'}</b>{' '}
            pracovný príkaz?
          </div>
        ),
      },
    ],
    [workOrder.locked]
  );

  usePrompts(
    [
      {
        key: `WORK_ORDER_UNPLAN_ALERT`,
        dialogTitle: 'Odplánovať pracovný príkaz',
        dialogShowClose: true,
        dialogShowConfirm: true,
        dialogWidth: 400,
        dialogText: (
          <div>
            Naozaj chcete <b>odplánovať</b> pracovný príkaz?
          </div>
        ),
      },
    ],
    []
  );

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={(e) => {
        setAnchorEl(null);
      }}
      getContentAnchorEl={null}
      anchorOrigin={{
        vertical: 'top',
        horizontal: left === 0 ? 'right' : left,
      }}
    >
      <DetailsMenuItem closeMenu={closeMenu} workOrder={workOrder} />
      <OpenMenuItem closeMenu={closeMenu} workOrder={workOrder} />
      <ErrorsMenuItem closeMenu={closeMenu} workOrder={workOrder} />
      <RouteMenuItem closeMenu={closeMenu} workOrder={workOrder} />
      <LockWorkOrderMenuItem
        closeMenu={closeMenu}
        workOrder={workOrder}
        afterSubmit={() => {
          calendarSource?.yAxis.refreshData();
        }}
      />
      <UnplanWorkOrderMenuItem
        closeMenu={closeMenu}
        workOrder={workOrder}
        afterSubmit={() => {
          calendarSource?.yAxis.refreshData();
        }}
      />
      <StateMenuItem closeMenu={closeMenu} workOrder={workOrder} />
    </Menu>
  );
});
